import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from "@mui/material/styles";
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import { useNavigate } from 'react-router-dom';

const PAGES = [
  { label: 'Tickets', sub: 'Conversaciones activas', path: '/tickets', icon: 'ti-inbox' },
  { label: 'Kanban', sub: 'Vista de tablero', path: '/Kanban', icon: 'ti-layout-kanban' },
  { label: 'Dashboard', sub: 'Métricas', path: '/', icon: 'ti-chart-bar' },
  { label: 'Contactos', sub: 'Clientes', path: '/contacts', icon: 'ti-users' },
  { label: 'Reportes', sub: 'Historial', path: '/reports', icon: 'ti-file-analytics' },
  { label: 'Campañas', sub: 'Mensajes masivos', path: '/campaigns', icon: 'ti-speakerphone' },
  { label: 'Conexiones', sub: 'WhatsApp, Facebook', path: '/connections', icon: 'ti-plug' },
  { label: 'Usuarios', sub: 'Agentes', path: '/users', icon: 'ti-users' },
  { label: 'Configuración', sub: 'Sistema', path: '/settings', icon: 'ti-settings' },
  { label: 'Tags', sub: 'Etiquetas', path: '/tags', icon: 'ti-tag' },
  { label: 'FlowBuilder', sub: 'Flujos', path: '/flowbuilders', icon: 'ti-git-branch' },
  { label: 'Archivos', sub: 'Documentos', path: '/files', icon: 'ti-folder' },
];

const GlobalSearch = () => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const navigate = useNavigate();

  const filtered = PAGES.filter(p =>
    p.label.toLowerCase().includes(query.toLowerCase()) ||
    p.sub.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSelect = useCallback((path) => {
    navigate(path);
    setOpen(false);
    setQuery('');
    setSelected(0);
  }, [navigate]);

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;
      if (e.key === 'ArrowDown') setSelected(s => Math.min(s + 1, filtered.length - 1));
      if (e.key === 'ArrowUp') setSelected(s => Math.max(s - 1, 0));
      if (e.key === 'Enter' && filtered[selected]) handleSelect(filtered[selected].path);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, filtered, selected, handleSelect]);

  return (
    <Dialog
      open={open}
      onClose={() => { setOpen(false); setQuery(''); }}
      PaperProps={{ sx: {
        borderRadius: '14px',
        border: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`,
        boxShadow: `0 20px 60px ${theme.palette.mode==="dark"?"rgba(255,255,255,0.15)":"rgba(0,0,0,0.15)"}`,
        width: '560px',
        maxWidth: '90vw',
        overflow: 'hidden',
        mt: '-20vh',
      }}}
      BackdropProps={{ sx: { backdropFilter: 'blur(4px)', background: (theme.palette.mode==="dark"?"rgba(255,255,255,0.3)":"rgba(0,0,0,0.3)") }}}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: '16px', borderBottom: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, height: 52 }}>
        <i className="ti ti-search" style={{ fontSize: 18, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)"), marginRight: 10 }} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Buscar páginas..."
          value={query}
          onChange={e => { setQuery(e.target.value); setSelected(0); }}
          sx={{ fontSize: 15, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.82)":"rgba(0,0,0,0.82)") }}
        />
        <Box sx={{ background: theme.palette.mode==="dark"?"rgba(255,255,255,0.04)":"#f8f7f5", border: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.1)":"rgba(0,0,0,0.1)"}`, borderRadius: '6px', px: '8px', py: '2px', fontSize: 11, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)") }}>
          ESC
        </Box>
      </Box>
      <Box sx={{ maxHeight: 360, overflowY: 'auto', py: '6px' }}>
        {filtered.length === 0 ? (
          <Box sx={{ py: '24px', textAlign: 'center' }}>
            <Typography sx={{ fontSize: 13, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.4)":"rgba(0,0,0,0.4)") }}>Sin resultados</Typography>
          </Box>
        ) : filtered.map((item, i) => (
          <Box
            key={item.path}
            onClick={() => handleSelect(item.path)}
            sx={{
              display: 'flex', alignItems: 'center', gap: '12px',
              px: '16px', py: '10px', cursor: 'pointer',
              background: i === selected ? 'rgba(3,153,255,0.06)' : 'transparent',
              '&:hover': { background: 'rgba(3,153,255,0.06)' },
            }}
          >
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: i === selected ? '#eff6ff' : '#f8f7f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={`ti ${item.icon}`} style={{ fontSize: 16, color: i === selected ? '#0399FF' : (theme.palette.mode==="dark"?"rgba(255,255,255,0.45)":"rgba(0,0,0,0.45)") }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500, color: i === selected ? '#0399FF' : (theme.palette.mode==="dark"?"rgba(255,255,255,0.75)":"rgba(0,0,0,0.75)") }}>{item.label}</Typography>
              <Typography sx={{ fontSize: 11, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.38)":"rgba(0,0,0,0.38)") }}>{item.sub}</Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ px: '16px', py: '8px', borderTop: `0.5px solid ${theme.palette.mode==="dark"?"rgba(255,255,255,0.07)":"rgba(0,0,0,0.07)"}`, display: 'flex', gap: '16px' }}>
        <Typography sx={{ fontSize: 11, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.35)") }}>↑↓ navegar</Typography>
        <Typography sx={{ fontSize: 11, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.35)") }}>↵ abrir</Typography>
        <Typography sx={{ fontSize: 11, color: (theme.palette.mode==="dark"?"rgba(255,255,255,0.35)":"rgba(0,0,0,0.35)") }}>ESC cerrar</Typography>
      </Box>
    </Dialog>
  );
};

export default GlobalSearch;
